/**
 * US holidays, computed. No rows, no columns, no network.
 *
 * ⚠️ NO `#N` REFERENCES ANYWHERE IN THIS FEATURE, deliberately. Kasandra
 * numbers her fast-path items 1, 2, 3… and this was item 8, with the
 * dispatcher half as item 9 — but `#N` means a GitHub number in this repo, and
 * issues and PRs share one sequence. `#8` is a closed "Repo cleanup" issue and
 * `#9` a closed "Morning Notes dispatcher fix" doc, so writing them would
 * cross-link every commit and PR to two unrelated closed items. Her item
 * numbers are spelled out in prose instead; do not "tidy" them into `#8`/`#9`.
 *
 * In `_shared/` because BOTH halves need the SAME list. The calendar draws a
 * chip on Halloween and the dispatcher (fast-path item 9) has to decide whether to text her
 * about it a week earlier — and those two must not disagree about which days
 * are holidays, what they are called, or how much warning each one deserves.
 * `src/lib/holidays.ts` is a re-export shim so the app imports `@/lib/holidays`
 * and nothing in `src/` knows this file moved sides. Same direction as
 * `_shared/payDay.ts` and `_shared/billPaycheckMatch.ts`.
 *
 * ⚠️ ZERO imports, deliberately. Deno resolves this file directly, cannot reach
 * into `src/`, and cannot read Vite's `@/` alias. A dependency added here is a
 * dependency added to every Edge Function that touches a holiday.
 *
 * ── WHY NOT `date-holidays` ─────────────────────────────────────────────────
 *
 * Measured on 2026-10-31, v3.38.0: 1.4 MB raw / 237 KB gzipped once bundled,
 * because it ships `data/holidays.json` — every one of ~200 countries — plus
 * `lodash` and `js-yaml`, and loads the whole file whatever country you ask
 * for. That is the entire weight of a second app for twenty-one dates a year.
 *
 * It is also wrong on some of what we want: Election Day is absent in 2026 (it
 * has one in 2028 only) and Presidents' Day is filed as "Washington's
 * Birthday". It emits extras we would filter (Tax Day, Administrative
 * Professionals Day, Day after Thanksgiving) and "(substitute day)" duplicates
 * we would dedupe — so the table below gets written either way. This way it is
 * not written twice.
 *
 * It remains the oracle, though. Every date in the ORIGINAL TWENTY was diffed
 * against it for 2025-2035 before shipping: 211 date pairs, zero mismatches.
 * That diff ran in a scratch directory and the package was never added to
 * `package.json` — see the header of `src/lib/__tests__/holidays.test.ts` for
 * what was compared and for the anchors that cover what the library lacks.
 *
 * ⚠️ GRANDPARENTS' DAY (added 2026-11-01) IS NOT IN THAT 211. The diff has not
 * been re-run and the number has deliberately not been inflated to cover a
 * comparison nobody made — see rule 9 in CLAUDE.md. It is pinned instead by
 * hand-computed anchors in the test file, including the two years where the
 * obvious shortcut for its rule is wrong. If you re-run the oracle diff, update
 * this number from the output rather than by arithmetic.
 *
 * ⚠️ NEVER `new Date("YYYY-MM-DD")`. That parses as UTC, so every timezone west
 * of UTC reads the day back one short — Halloween renders on the 30th in
 * America/New_York. Every date below is built with the local multi-argument
 * `Date` constructor and formatted by hand; `toISOString` is never used,
 * because it converts to UTC on the way out and reintroduces the same shift.
 */

/** Stable key. Safe to persist and to compare; the display name is not. */
export type HolidayId =
  | "new_years_day"
  | "mlk_day"
  | "presidents_day"
  | "memorial_day"
  | "juneteenth"
  | "independence_day"
  | "labor_day"
  | "grandparents_day"
  | "columbus_day"
  | "veterans_day"
  | "thanksgiving"
  | "christmas"
  | "valentines_day"
  | "st_patricks_day"
  | "easter"
  | "mothers_day"
  | "fathers_day"
  | "halloween"
  | "christmas_eve"
  | "new_years_eve"
  | "election_day";

/**
 * `federal` closes schools and offices; `observance` does not. The calendar
 * renders both identically — the distinction is here for the dispatcher half,
 * which has a real
 * reason to care (a closure is a childcare problem, Valentine's Day is not).
 */
export type HolidayKind = "federal" | "observance";

export interface UsHoliday {
  id: HolidayId;
  /** Display name. Shown in chips and in the day sheet. */
  name: string;
  /** Exactly one emoji. Month cells have room for this and nothing else. */
  emoji: string;
  kind: HolidayKind;
  /**
   * Holiday prep (fast-path item 9) — how many days of warning this holiday actually needs. Costumes and
   * gifts need a week or three; a Monday closure needs a few days. 0 means
   * "the day itself is the first useful mention".
   *
   * ⚠️ These are defaults chosen to be defensible, not tuned. That item owns the
   * final numbers — but it must read THESE, not a second copy, or the calendar
   * and the text message will disagree about when Christmas prep starts.
   */
  leadDays: number;
  /**
   * Holiday prep (fast-path item 9) — whether Kin should proactively text about this one. True only where
   * there is something to BUY or MAKE ahead of time. A federal closure is
   * informational: it belongs on the calendar, not in an SMS.
   */
  prepViaSms: boolean;
}

/**
 * When a holiday falls, as a rule rather than a date.
 *
 * `fixed`      — same month/day every year (Christmas).
 * `nthWeekday` — the Nth given weekday of a month, `nth: -1` for the last one
 *                (Memorial Day is the LAST Monday in May, not the fourth —
 *                May has five Mondays in 2026 and the fourth is the 25th only
 *                by luck).
 * `easter`     — Easter Sunday itself; `offsetDays` is there so Good Friday or
 *                Ash Wednesday can be added later without a new rule kind.
 * `electionDay`— first Tuesday AFTER the first Monday in November. Not simply
 *                "first Tuesday": when Nov 1 is a Tuesday the election is on
 *                the 8th.
 */
type HolidayRule =
  | { type: "fixed"; month: number; day: number }
  | { type: "nthWeekday"; month: number; weekday: number; nth: number }
  | { type: "easter"; offsetDays: number }
  | { type: "electionDay" }
  | { type: "sundayAfterLaborDay" };

interface HolidayDef extends UsHoliday {
  rule: HolidayRule;
  /**
   * Years this holiday exists at all. Election Day is the federal general
   * election, which is even-numbered years only — emitting one every year
   * would put a voting chip on a day with no election on it.
   */
  years?: (year: number) => boolean;
}

const MON = 1;
const TUE = 2;
const THU = 4;
const SUN = 0;

/**
 * The whole list. Order here is not significant — `holidaysInRange` sorts by
 * date — but keeping federal and observance grouped makes the table readable.
 */
const HOLIDAYS: readonly HolidayDef[] = [
  // ── Federal ───────────────────────────────────────────────────────────────
  {
    id: "new_years_day",
    name: "New Year's Day",
    emoji: "🎉",
    kind: "federal",
    leadDays: 0,
    prepViaSms: false,
    rule: { type: "fixed", month: 1, day: 1 },
  },
  {
    id: "mlk_day",
    name: "Martin Luther King Jr. Day",
    emoji: "🕊️",
    kind: "federal",
    leadDays: 3,
    prepViaSms: false,
    rule: { type: "nthWeekday", month: 1, weekday: MON, nth: 3 },
  },
  {
    id: "presidents_day",
    name: "Presidents' Day",
    emoji: "🎩",
    kind: "federal",
    leadDays: 3,
    prepViaSms: false,
    rule: { type: "nthWeekday", month: 2, weekday: MON, nth: 3 },
  },
  {
    id: "memorial_day",
    name: "Memorial Day",
    emoji: "🎖️",
    kind: "federal",
    leadDays: 3,
    prepViaSms: false,
    rule: { type: "nthWeekday", month: 5, weekday: MON, nth: -1 },
  },
  {
    id: "juneteenth",
    name: "Juneteenth",
    emoji: "✊🏿",
    kind: "federal",
    leadDays: 3,
    prepViaSms: false,
    rule: { type: "fixed", month: 6, day: 19 },
  },
  {
    id: "independence_day",
    name: "Independence Day",
    emoji: "🎆",
    kind: "federal",
    leadDays: 7,
    prepViaSms: true,
    rule: { type: "fixed", month: 7, day: 4 },
  },
  {
    id: "labor_day",
    name: "Labor Day",
    emoji: "🛠️",
    kind: "federal",
    leadDays: 3,
    prepViaSms: false,
    rule: { type: "nthWeekday", month: 9, weekday: MON, nth: 1 },
  },
  {
    // Added 2026-11-01, Kasandra's call. It was one of the occasions #1668 named
    // as missing from the gift heads-up, and unlike Hanukkah — the other one —
    // it is a plain Gregorian rule this module can already express, so it costs
    // nothing to hold. Hanukkah is on the Hebrew lunar calendar and would need
    // either a dependency or a date table that goes stale in silence; it is
    // deliberately NOT here and has its own issue.
    //
    // 10 days and an SMS, matching Mother's/Father's Day: it is a card-and-gift
    // occasion with the same shipping problem, and grandparents are the people
    // most likely to be posted to rather than handed something.
    id: "grandparents_day",
    name: "Grandparents' Day",
    emoji: "🧓",
    kind: "observance",
    leadDays: 10,
    prepViaSms: true,
    rule: { type: "sundayAfterLaborDay" },
  },
  {
    // ⚠️ KASANDRA'S CALL, 2026-11-01. This shipped as "Indigenous Peoples' Day"
    // and she changed it back to "Columbus Day". Her brief had said
    // "Columbus/Indigenous Peoples' Day" and a slashed name does not fit a
    // chip, so the original pick was mine, not hers — and it was the wrong one
    // to make quietly. If it comes up again it is a voice decision, not a
    // technical one: ask rather than re-deciding from the federal name.
    //
    // The DATE is the same either way (second Monday of October), so this is a
    // display change only. The id moved with it because nothing persists a
    // holiday id — the table is recomputed from integers on every read.
    id: "columbus_day",
    name: "Columbus Day",
    emoji: "⛵",
    kind: "federal",
    leadDays: 3,
    prepViaSms: false,
    rule: { type: "nthWeekday", month: 10, weekday: MON, nth: 2 },
  },
  {
    id: "veterans_day",
    name: "Veterans Day",
    emoji: "🇺🇸",
    kind: "federal",
    leadDays: 3,
    prepViaSms: false,
    rule: { type: "fixed", month: 11, day: 11 },
  },
  {
    id: "thanksgiving",
    name: "Thanksgiving",
    emoji: "🦃",
    kind: "federal",
    leadDays: 14,
    prepViaSms: true,
    rule: { type: "nthWeekday", month: 11, weekday: THU, nth: 4 },
  },
  {
    id: "christmas",
    name: "Christmas",
    emoji: "🎄",
    kind: "federal",
    leadDays: 21,
    prepViaSms: true,
    rule: { type: "fixed", month: 12, day: 25 },
  },

  // ── Observances she actually plans around ─────────────────────────────────
  {
    id: "valentines_day",
    name: "Valentine's Day",
    emoji: "💘",
    kind: "observance",
    leadDays: 7,
    prepViaSms: true,
    rule: { type: "fixed", month: 2, day: 14 },
  },
  {
    id: "st_patricks_day",
    name: "St. Patrick's Day",
    emoji: "☘️",
    kind: "observance",
    // Three days is enough to find something green before school.
    leadDays: 3,
    prepViaSms: true,
    rule: { type: "fixed", month: 3, day: 17 },
  },
  {
    id: "easter",
    name: "Easter",
    emoji: "🐣",
    kind: "observance",
    leadDays: 7,
    prepViaSms: true,
    rule: { type: "easter", offsetDays: 0 },
  },
  {
    id: "mothers_day",
    name: "Mother's Day",
    emoji: "💐",
    kind: "observance",
    leadDays: 10,
    prepViaSms: true,
    rule: { type: "nthWeekday", month: 5, weekday: SUN, nth: 2 },
  },
  {
    id: "fathers_day",
    name: "Father's Day",
    emoji: "👔",
    kind: "observance",
    leadDays: 10,
    prepViaSms: true,
    rule: { type: "nthWeekday", month: 6, weekday: SUN, nth: 3 },
  },
  {
    id: "halloween",
    name: "Halloween",
    emoji: "🎃",
    kind: "observance",
    // ⚠️ 21, not the 7 this shipped with (changed 2026-11-01). Halloween is the
    // one observance here whose prep has a HARD supply deadline rather than a
    // shipping one: costumes in the size her kid actually wears are gone by
    // mid-October, so a reminder on the 24th is not early, it is an apology.
    // Three weeks puts it around the 10th, while choosing is still possible.
    // The other 7-day holidays were reviewed at the same time and left alone —
    // flowers and baskets can be bought the same week.
    leadDays: 21,
    prepViaSms: true,
    rule: { type: "fixed", month: 10, day: 31 },
  },
  {
    id: "christmas_eve",
    name: "Christmas Eve",
    emoji: "🎁",
    kind: "observance",
    // No lead and no SMS: Christmas itself carries 21 days of warning, and two
    // prep threads for one weekend is nagging.
    leadDays: 0,
    prepViaSms: false,
    rule: { type: "fixed", month: 12, day: 24 },
  },
  {
    id: "new_years_eve",
    name: "New Year's Eve",
    emoji: "🥂",
    kind: "observance",
    leadDays: 3,
    prepViaSms: true,
    rule: { type: "fixed", month: 12, day: 31 },
  },
  {
    id: "election_day",
    name: "Election Day",
    emoji: "🗳️",
    kind: "observance",
    leadDays: 7,
    prepViaSms: true,
    rule: { type: "electionDay" },
    years: (year) => year % 2 === 0,
  },
];

/** A holiday with the year resolved. `date` is a local YYYY-MM-DD. */
export interface DatedHoliday extends UsHoliday {
  date: string;
}

/**
 * Local YYYY-MM-DD. Hand-formatted rather than `toISOString().slice(0, 10)`,
 * which would convert to UTC first and move the date west of UTC.
 */
export function formatHolidayDate(year: number, month: number, day: number): string {
  const mm = month < 10 ? `0${month}` : String(month);
  const dd = day < 10 ? `0${day}` : String(day);
  return `${year}-${mm}-${dd}`;
}

/** Day of week (0 = Sunday) for a local calendar date. */
function weekdayOf(year: number, month: number, day: number): number {
  // Local multi-argument constructor: no string parsing, so no UTC step.
  return new Date(year, month - 1, day).getDay();
}

/** Days in a month. Day 0 of the NEXT month is the last day of this one. */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

/**
 * The Nth `weekday` of a month, or the last one when `nth` is -1.
 *
 * ⚠️ Counting forward 7 * (nth - 1) days from the first matching weekday can
 * run off the end of the month for nth 5, so the result is clamped by
 * `daysInMonth` rather than allowed to roll into the next month — which is
 * what plain `Date` arithmetic would do, silently.
 */
function nthWeekdayOfMonth(year: number, month: number, weekday: number, nth: number): number {
  if (nth === -1) {
    const last = daysInMonth(year, month);
    const lastWeekday = weekdayOf(year, month, last);
    // How far back from the last day to the previous `weekday`.
    const backoff = (lastWeekday - weekday + 7) % 7;
    return last - backoff;
  }
  const firstWeekday = weekdayOf(year, month, 1);
  const firstMatch = 1 + ((weekday - firstWeekday + 7) % 7);
  const day = firstMatch + (nth - 1) * 7;
  return day > daysInMonth(year, month) ? firstMatch + (nth - 2) * 7 : day;
}

/**
 * Easter Sunday, Gregorian. The Anonymous Gregorian computus (Meeus/Jones/
 * Butcher) — integer arithmetic only, valid for all Gregorian years, and the
 * reason this module needs no dependency at all.
 */
function easterSunday(year: number): { month: number; day: number } {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { month, day };
}

/**
 * First Tuesday AFTER the first Monday in November — which is not the same as
 * the first Tuesday. When Nov 1 is a Tuesday, the first Monday is the 7th and
 * the election is the 8th.
 */
function electionDay(year: number): number {
  const firstMonday = nthWeekdayOfMonth(year, 11, MON, 1);
  return firstMonday + 1;
}

/**
 * First Sunday AFTER Labor Day — Grandparents' Day.
 *
 * ⚠️ THIS IS NOT "the second Sunday of September", and that shortcut is wrong by
 * a full week in real years. It gets its own case for the same reason
 * `electionDay` does: the rule is anchored to another weekday's position, not to
 * an ordinal.
 *
 * Labor Day is the first Monday, so it falls Sep 1-7 and this lands Sep 7-13.
 * When Sep 1 IS a Monday, Labor Day is the 1st and the next Sunday is the 7th —
 * which is that month's FIRST Sunday, so an `nth: 2` rule would return the 14th:
 *
 *   2025  Sep 1 = Mon   Labor Sep 1   Grandparents Sep 7    2nd Sunday = Sep 14  ✗
 *   2031  Sep 1 = Mon   Labor Sep 1   Grandparents Sep 7    2nd Sunday = Sep 14  ✗
 *   2026  Sep 1 = Tue   Labor Sep 7   Grandparents Sep 13   2nd Sunday = Sep 13  ✓
 *
 * Every other weekday for Sep 1 makes the two agree, which is exactly what makes
 * the shortcut look correct if you spot-check one year. `holidays.test.ts` pins
 * 2025 and 2031 specifically.
 */
function sundayAfterLaborDay(year: number): number {
  return nthWeekdayOfMonth(year, 9, MON, 1) + 6;
}

/** Resolve one rule to a month/day in `year`. */
function resolveRule(rule: HolidayRule, year: number): { month: number; day: number } {
  switch (rule.type) {
    case "fixed":
      return { month: rule.month, day: rule.day };
    case "nthWeekday":
      return { month: rule.month, day: nthWeekdayOfMonth(year, rule.month, rule.weekday, rule.nth) };
    case "easter": {
      const base = easterSunday(year);
      if (rule.offsetDays === 0) return base;
      // Offsets can cross a month boundary, so go through a real date.
      const d = new Date(year, base.month - 1, base.day + rule.offsetDays);
      return { month: d.getMonth() + 1, day: d.getDate() };
    }
    case "electionDay":
      return { month: 11, day: electionDay(year) };
    case "sundayAfterLaborDay":
      return { month: 9, day: sundayAfterLaborDay(year) };
  }
}

/**
 * Every holiday in one calendar year, sorted by date.
 *
 * ⚠️ Not memoised on purpose. Twenty-one entries of integer arithmetic is cheaper
 * than a cache that can go stale across a year boundary while the app is open
 * overnight — which is exactly when a New Year's Eve chip has to appear.
 */
export function holidaysInYear(year: number): DatedHoliday[] {
  const out: DatedHoliday[] = [];
  for (const def of HOLIDAYS) {
    if (def.years && !def.years(year)) continue;
    const { month, day } = resolveRule(def.rule, year);
    out.push({
      id: def.id,
      name: def.name,
      emoji: def.emoji,
      kind: def.kind,
      leadDays: def.leadDays,
      prepViaSms: def.prepViaSms,
      date: formatHolidayDate(year, month, day),
    });
  }
  out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  return out;
}

/**
 * Every holiday from `startYmd` to `endYmd`, inclusive. Both are local
 * YYYY-MM-DD; string comparison is correct for that format and needs no
 * parsing, which is the point of keeping dates as strings here.
 *
 * Spans the years the range actually covers, so a Dec 28 → Jan 3 week gets
 * both New Year's Eve and New Year's Day.
 */
export function holidaysInRange(startYmd: string, endYmd: string): DatedHoliday[] {
  if (!startYmd || !endYmd || startYmd > endYmd) return [];
  const startYear = Number(startYmd.slice(0, 4));
  const endYear = Number(endYmd.slice(0, 4));
  if (!Number.isFinite(startYear) || !Number.isFinite(endYear)) return [];
  const out: DatedHoliday[] = [];
  for (let y = startYear; y <= endYear; y += 1) {
    for (const h of holidaysInYear(y)) {
      if (h.date >= startYmd && h.date <= endYmd) out.push(h);
    }
  }
  return out;
}

/**
 * A date → holidays map for the views, which look up one cell at a time.
 *
 * An array value even though NO two holidays in the table above can currently
 * share a date — Easter, the only movable one, ranges Mar 22 to Apr 25 and
 * meets nothing. The shape is for what gets added later: Good Friday, a
 * religious observance on a lunar calendar, a state holiday. A caller written
 * against a bare `DatedHoliday` would have to change at that point, and the
 * one that got missed would silently drop a holiday instead of failing.
 */
export function holidaysByDate(startYmd: string, endYmd: string): Map<string, DatedHoliday[]> {
  const map = new Map<string, DatedHoliday[]>();
  for (const h of holidaysInRange(startYmd, endYmd)) {
    const existing = map.get(h.date);
    if (existing) existing.push(h);
    else map.set(h.date, [h]);
  }
  return map;
}

/** The holiday on one specific local date, or null. */
export function holidayOn(ymd: string): DatedHoliday | null {
  if (!ymd) return null;
  const day = ymd.slice(0, 10);
  const year = Number(day.slice(0, 4));
  if (!Number.isFinite(year)) return null;
  for (const h of holidaysInYear(year)) {
    if (h.date === day) return h;
  }
  return null;
}

/**
 * One line per holiday, for a model prompt. Explicitly labelled as a holiday
 * and explicitly NOT one of her events, because the prompts this feeds also
 * carry her real schedule and the model must not merge the two or echo a
 * holiday back as something she has to do.
 */
export function formatHolidayLine(h: DatedHoliday): string {
  return `- ${h.date}: ${h.emoji} ${h.name}`;
}
